import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-22 — adoption metrics for administrators (counts only, no meeting content).
 * Shown at the top of the BD module's Activity Feed tab. GET /api/bd-assistant/admin/adoption.
 */

interface CaptureRow { detected: number; captured: number; no_notes: number; not_bd: number; no_answer: number; capture_rate: number | null }
interface Approvals {
  action_type: string; proposed: number; approved: number; edited: number; rejected: number; expired: number; waiting: number; replaced: number;
  approve_rate: number | null; edit_rate: number | null; reject_rate: number | null;
}
interface Metrics {
  window: { days: number };
  capture: CaptureRow & { people: (CaptureRow & { user_id: number; name: string | null })[] };
  followUp: { approved: number; median_hours: number | null; p75_hours: number | null };
  approvals: Approvals[];
  draftEdits: { edits: number; avg_words_changed: number | null; weeks: { week: string; edits: number; avg_words_changed: number | null }[] };
  zohoFreshness: { available: boolean; reason?: string; as_of?: string; fresh_days?: number; open_deals?: number; fresh?: number; fresh_rate?: number | null };
  radar: { available: boolean; reason: string };
}

const ACTION_LABELS: Record<string, string> = {
  zoho_note: 'Zoho note', zoho_field_update: 'Zoho field update', zoho_contact_create: 'Zoho contact', email_draft: 'Follow-up draft',
  pm_task: 'PM task', pm_comment: 'PM comment', bd_person_merge: 'Merge contacts', bd_org_merge: 'Merge organisations', bd_skip_rule: 'Skip rule',
};
const pct = (v: number | null | undefined) => (v == null ? '—' : `${v}%`);
const hours = (v: number | null) => (v == null ? '—' : v < 48 ? `${v} h` : `${Math.round(v / 24)} days`);

const Card: React.FC<{ title: string; value: string; note?: string }> = ({ title, value, note }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3">
    <div className="text-xs text-gray-500">{title}</div>
    <div className="text-xl font-semibold text-gray-900">{value}</div>
    {note && <div className="text-xs text-gray-500 mt-0.5">{note}</div>}
  </div>
);

const AdoptionPanel: React.FC = () => {
  const [days, setDays] = useState(30);
  const [m, setM] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/bd-assistant/admin/adoption', { params: { days } });
      setM(data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not load the adoption metrics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-4" data-testid="bd-adoption">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900"><BarChart3 className="w-4 h-4" /> Adoption</div>
        <select className="text-sm rounded border border-gray-300 px-2 py-1" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="bd-adoption-days">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {loading && <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

      {!loading && m && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card title="Meetings captured" value={pct(m.capture.capture_rate)} note={`${m.capture.captured} of ${m.capture.detected} external meetings`} />
            <Card title="Meeting end → approved follow-up" value={hours(m.followUp.median_hours)} note={m.followUp.approved ? `median of ${m.followUp.approved} · 75% within ${hours(m.followUp.p75_hours)}` : 'no approved follow-ups yet'} />
            <Card title="Words changed per draft edit" value={m.draftEdits.avg_words_changed == null ? '—' : String(m.draftEdits.avg_words_changed)} note={`${m.draftEdits.edits} edited drafts · should fall over time`} />
            <Card
              title={`Zoho Next Steps updated in ${m.zohoFreshness.fresh_days || 14} days`}
              value={m.zohoFreshness.available ? pct(m.zohoFreshness.fresh_rate) : '—'}
              note={m.zohoFreshness.available ? `${m.zohoFreshness.fresh} of ${m.zohoFreshness.open_deals} open deals · as of the ${m.zohoFreshness.as_of} review` : m.zohoFreshness.reason}
            />
          </div>

          <div className="text-xs text-gray-600">
            Not captured: {m.capture.no_notes} answered "no notes", {m.capture.not_bd} "not a BD meeting", {m.capture.no_answer} no answer.
          </div>

          {m.capture.people.length > 0 && (
            <table className="min-w-full text-sm" data-testid="bd-adoption-people">
              <thead className="text-xs text-gray-500 uppercase"><tr><th className="text-left py-1">Person</th><th className="text-right">External meetings</th><th className="text-right">Captured</th><th className="text-right">Rate</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {m.capture.people.map((p) => (
                  <tr key={p.user_id}><td className="py-1">{p.name || `User ${p.user_id}`}</td><td className="text-right">{p.detected}</td><td className="text-right">{p.captured}</td><td className="text-right">{pct(p.capture_rate)}</td></tr>
                ))}
              </tbody>
            </table>
          )}

          <table className="min-w-full text-sm" data-testid="bd-adoption-approvals">
            <thead className="text-xs text-gray-500 uppercase">
              <tr><th className="text-left py-1">Proposal</th><th className="text-right">Proposed</th><th className="text-right">Approved</th><th className="text-right">Edited</th><th className="text-right">Rejected</th><th className="text-right">Expired</th><th className="text-right">Replaced</th><th className="text-right">Waiting</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {m.approvals.map((a) => (
                <tr key={a.action_type}>
                  <td className="py-1">{ACTION_LABELS[a.action_type] || a.action_type}</td>
                  <td className="text-right">{a.proposed}</td>
                  <td className="text-right">{a.approved} <span className="text-gray-400">{pct(a.approve_rate)}</span></td>
                  <td className="text-right">{a.edited} <span className="text-gray-400">{pct(a.edit_rate)}</span></td>
                  <td className="text-right">{a.rejected} <span className="text-gray-400">{pct(a.reject_rate)}</span></td>
                  <td className="text-right">{a.expired}</td>
                  <td className="text-right">{a.replaced}</td>
                  <td className="text-right">{a.waiting}</td>
                </tr>
              ))}
              {!m.approvals.length && <tr><td colSpan={8} className="py-2 text-gray-400">No proposals in this period.</td></tr>}
            </tbody>
          </table>

          {m.draftEdits.weeks.length > 1 && (
            <div className="text-xs text-gray-600">
              Words changed per edit, by week: {m.draftEdits.weeks.map((w) => `${w.week}: ${w.avg_words_changed}`).join(' · ')}
            </div>
          )}

          <div className="text-xs text-gray-400">Radar usefulness votes: {m.radar.reason}</div>
        </>
      )}
    </div>
  );
};

export default AdoptionPanel;
